/**
 * PipelineLegacyBoard — the ORIGINAL contact-based kanban, preserved verbatim.
 *
 * ⚠⚠ THIS BOARD SHOWS CONTACTS, NOT OPPORTUNITIES, AND MOVING A CARD HERE
 * CHANGES NOTHING THAT ANY REPORT COUNTS.
 *
 * It buckets crm_contacts by the free-text crm_contacts.pipeline_stage.
 * crm_contacts has NO pipeline_id, so nothing scopes these cards to a pipeline.
 * Its drag writes crm_contacts.pipeline_stage; every KPI figure — open
 * opportunity count, weighted value, at-risk — is computed from
 * crm_opportunities. Proven live on 2026-09-06: a card was dragged on
 * Carlos's Report and the KPI tile was BYTE-IDENTICAL before and after
 * (7730 opportunities, $841,486, 5,498 at-risk). The card moves; the report
 * does not.
 *
 * It is kept ONLY so nobody loses a working affordance the week the
 * opportunity board ships. It retires when drag lands on the new board, which
 * is gated on scoping the zgx_touched_at latch (currently ROW-scoped: one
 * drag would freeze value, contact name, status and last activity on that
 * opportunity against GHL, permanently).
 *
 * Extracted unchanged from PipelineTab as of 99e11c9. Do not extend it.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Mail, Phone, AlertTriangle, GripVertical } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import { neuShadowSm, CONTACT_TYPES } from './crmShared';
import { DealHealthChip, NextBestActionChip } from './PipelineIntelligence';

const MAX_CARDS_PER_STAGE = 50;

export default function PipelineLegacyBoard({
  pipeline, contacts, loadData, loadExtended, onContactClick, userRole,
}) {
  const kanbanRef = useRef(null);
  const { onKeyDown: handleKanbanKeyDown } = useArrowNav(kanbanRef, '[data-stage-column]', { horizontal: true, vertical: false });
  const dragMovedRef = useRef(false);
  const setHasDragMoved = (v) => { dragMovedRef.current = v; };
  const [dragOverStage, setDragOverStage] = useState(null);
  const [draggingContactId, setDraggingContactId] = useState(null);

  const handleDragStart = useCallback((e, contactId) => {
    e.dataTransfer.setData('contactId', contactId.toString());
    e.dataTransfer.effectAllowed = 'move';
    setDraggingContactId(contactId);
  }, []);
  const handleDragEnd = useCallback(() => { setDraggingContactId(null); setDragOverStage(null); }, []);

  const stages = pipeline.stages || [];
  const claimsUnstaged = !!pipeline.is_default;
  const { mappedContacts, totalContacts, orphaned } = useMemo(() => {
    const mapped = stages.reduce((acc, stg, i) => {
      acc[stg.name] = (contacts || []).filter((c) =>
        c.pipeline_stage === stg.name ||
        (claimsUnstaged && i === 0 && (c.pipeline_stage === 'new' || !c.pipeline_stage))
      );
      return acc;
    }, {});
    const flat = Object.values(mapped).flat();
    const mappedIds = new Set(flat.map((c) => c.id));
    return { mappedContacts: mapped, totalContacts: flat.length,
      orphaned: (contacts || []).filter((c) => !mappedIds.has(c.id)) };
  }, [contacts, stages, claimsUnstaged]);

  return (
    <div>
      {/* ⚠ HONEST LABEL, IN THE VIEW — not a tooltip. */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', background: '#FEF3C7',
        border: '1px solid #F59E0B', borderRadius: 10, padding: '8px 12px', marginBottom: 10,
      }}>
        <AlertTriangle size={14} color="#B45309" style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: '#92400E', lineHeight: 1.5 }}>
          <strong>Legacy view — these are CONTACTS, not opportunities.</strong>
          <div style={{ marginTop: 2 }}>
            Moving a card here changes a contact’s stage label only. It does <strong>not</strong> change the
            opportunity count, pipeline value or at-risk figures — those are computed from opportunities.
            Switch to <strong>Board</strong> for the records the reports count. {totalContacts} contact(s) shown.
          </div>
        </div>
      </div>
  {/* ─── Funnel Overview Row ─── */}
  <div style={{
    display: 'flex', gap: 0, marginBottom: 14, marginTop: 10,
    background: '#fff', borderRadius: 12, boxShadow: neuShadowSm, overflow: 'hidden',
  }}>
    {stages.map((stg, i) => {
      const count = (mappedContacts[stg.name] || []).length;
      const pct = totalContacts > 0 ? Math.round((count / totalContacts) * 100) : 0;
      return (
        <div key={stg.id} style={{
          flex: 1, padding: '10px 6px', textAlign: 'center',
          borderRight: i < stages.length - 1 ? '1px solid #f1f5f9' : 'none',
          background: count > 0 ? `${stg.color}08` : 'transparent',
          position: 'relative', minWidth: 0,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, background: stg.color,
            margin: '0 auto 4px', boxShadow: `0 0 0 2px ${stg.color}20`,
          }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: stg.color }}>{count}</div>
          <div style={{
            fontSize: 9, fontWeight: 600, color: '#6C757D', lineHeight: 1.2, marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={stg.name}>{stg.name}</div>
          {pct > 0 && <div style={{ fontSize: 8, color: '#ADB5BD', marginTop: 1 }}>{pct}%</div>}
          {stg.is_win && <div style={{ fontSize: 8, color: '#62BB46', fontWeight: 700, marginTop: 1 }}>WIN</div>}
          {stg.is_loss && <div style={{ fontSize: 8, color: '#DC3545', fontWeight: 700, marginTop: 1 }}>LOSS</div>}
          {i < stages.length - 1 && (
            <div style={{
              position: 'absolute', right: -5, top: '50%', transform: 'translateY(-50%)',
              width: 0, height: 0, borderTop: '4px solid transparent', borderBottom: '4px solid transparent',
              borderLeft: `5px solid ${stg.color}40`, zIndex: 2,
            }} />
          )}
        </div>
      );
    })}
  </div>

  {/* ─── Kanban Board ─── */}
  <div
    ref={kanbanRef}
    onKeyDown={handleKanbanKeyDown}
    style={{
      display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10, minHeight: 240,
    }}
  >
    {stages.map((stg) => {
      const stgContacts = mappedContacts[stg.name] || [];
      const isDropTarget = dragOverStage === stg.name;
      return (
        <div key={stg.id} data-stage-column tabIndex={0} style={{
          minWidth: 210, width: 210, flex: '0 0 210px',
          background: isDropTarget ? `${stg.color}12` : '#fff',
          borderRadius: 12,
          border: `2px ${isDropTarget ? 'dashed' : 'solid'} ${isDropTarget ? stg.color : `${stg.color}30`}`,
          display: 'flex', flexDirection: 'column', maxHeight: 440, outline: 'none',
          transition: 'all .15s ease', transform: isDropTarget ? 'scale(1.02)' : 'none',
        }}
          onFocus={(e) => { e.currentTarget.style.boxShadow = `${neuShadowSm}, 0 0 0 2px #2E8B98`; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverStage(stg.name); }}
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStage(null);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            setDragOverStage(null);
            setDraggingContactId(null);
            const cid = e.dataTransfer.getData('contactId');
            if (cid) {
              await window.zignagenixAPI.moveContactStage(parseInt(cid), stg.name);
              loadData();
              loadExtended();
            }
          }}
        >
          {/* Stage header */}
          <div style={{
            padding: '10px 12px', borderBottom: `2px solid ${stg.color}`,
            background: `${stg.color}08`, borderRadius: '10px 10px 0 0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: stg.color,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140,
              }} title={stg.name}>{stg.name}</span>
              <span style={{
                fontSize: 10, fontWeight: 700, background: `${stg.color}18`,
                color: stg.color, padding: '1px 6px', borderRadius: 8,
              }}>{stgContacts.length}</span>
            </div>
          </div>

          {/* Drop zone indicator */}
          {isDropTarget && (
            <div style={{
              padding: '8px 0', textAlign: 'center', fontSize: 11, fontWeight: 600,
              color: stg.color, background: `${stg.color}10`,
              borderBottom: `1px dashed ${stg.color}40`,
            }}>
              Drop here
            </div>
          )}

          {/* Contact cards */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
            {stgContacts.length === 0 && !isDropTarget ? (
              <div style={{ fontSize: 10, color: '#ADB5BD', textAlign: 'center', padding: 16 }}>No contacts</div>
            ) : stgContacts.slice(0, MAX_CARDS_PER_STAGE).map(c => {
              const ct = CONTACT_TYPES[c.contact_type] || CONTACT_TYPES.other;
              const isDragging = draggingContactId === c.id;
              return (
                <div key={c.id} draggable
                  onDragStart={(e) => {
                    // Phase G fix: drag must NOT initiate when the gesture
                    // started inside an AI chip (data-no-drag region).
                    // Without this check the chip click drags the card to
                    // whatever column the cursor crosses next.
                    if (e.target.closest && e.target.closest('[data-no-drag]')) {
                      e.preventDefault();
                      return;
                    }
                    setHasDragMoved(false);
                    handleDragStart(e, c.id);
                  }}
                  onDrag={() => setHasDragMoved(true)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => {
                    // Skip click handling if the click originated inside a
                    // no-drag region (AI chips have their own click logic).
                    if (e.target.closest && e.target.closest('[data-no-drag]')) return;
                    // Only treat as a click if the user didn't actually drag.
                    // Browsers fire 'click' AFTER dragend on a no-move pointer,
                    // so if dragMovedRef.current is true we ignore.
                    // ⚠ The EVENT is forwarded so a cmd/ctrl-click can still
                    // open the full record (Card P95 item 2). The drag guard
                    // above is unchanged — it already worked.
                    if (!dragMovedRef.current && onContactClick) onContactClick(c.id, { event: e });
                  }}
                  style={{
                    background: '#F8F9FA', borderRadius: 10, padding: '8px 10px', marginBottom: 6,
                    border: 'none', boxShadow: isDragging ? 'none' : neuShadowSm,
                    cursor: isDragging ? 'grabbing' : 'pointer',
                    transition: 'all .15s ease',
                    opacity: isDragging ? 0.4 : 1,
                  }}
                  onMouseEnter={e => { if (!isDragging) e.currentTarget.style.boxShadow = '7px 7px 14px rgba(174,190,210,0.6), -7px -7px 14px rgba(255,255,255,0.9)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = isDragging ? 'none' : neuShadowSm; }}
                  title="Click to view contact details — drag to change stage"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <GripVertical size={10} color="#CED4DA" style={{ flexShrink: 0 }} />
                    <div
                      style={{
                        fontSize: 12, fontWeight: 600, color: '#2E8B98',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {c.first_name} {c.last_name}
                    </div>
                  </div>
                  {c.organization && (
                    <div style={{ fontSize: 10, color: '#6C757D', marginBottom: 3, marginLeft: 14 }}>{c.organization}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: 14 }}>
                    <span style={{ fontSize: 9, color: ct.color, fontWeight: 600 }}>{ct.label}</span>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {c.email && <Mail size={10} color="#ADB5BD" />}
                      {c.phone && <Phone size={10} color="#ADB5BD" />}
                    </div>
                  </div>
                  {userRole && (
                    <div
                      data-no-drag="true"
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      draggable={false}
                      onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                      style={{ display: 'flex', gap: 4, marginTop: 5, marginLeft: 14, flexWrap: 'wrap' }}
                    >
                      <DealHealthChip contactId={c.id} role={userRole} />
                      <NextBestActionChip contactId={c.id} role={userRole} />
                    </div>
                  )}
                </div>
              );
            })}
            {stgContacts.length > MAX_CARDS_PER_STAGE && (
              <div style={{
                fontSize: 10, color: '#6C757D', textAlign: 'center',
                padding: '8px 0', fontWeight: 600,
              }}>
                +{stgContacts.length - MAX_CARDS_PER_STAGE} more — use search/filter to narrow
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>

  {/* ─── Orphaned Contacts ─── */}
  {orphaned.length > 0 && (
    <div style={{
      marginTop: 10, background: '#fff', borderRadius: 12,
      boxShadow: `${neuShadowSm}, inset 0 0 0 2px #fbbf24`, padding: 14,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <AlertTriangle size={14} /> {orphaned.length} contact(s) with unrecognized stage
      </div>
      {orphaned.map(c => (
        <div key={c.id} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '4px 0', fontSize: 12,
        }}>
          <span style={{ color: '#212529' }}>
            <span onClick={(e) => onContactClick && onContactClick(c.id, { event: e })} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
              {c.first_name} {c.last_name}
            </span> — <em style={{ color: '#92400e' }}>{c.pipeline_stage || '(none)'}</em>
          </span>
          {stages.length > 0 && (
            <select
              style={{
                fontSize: 11, padding: '4px 8px', borderRadius: 8,
                border: 'none', background: '#F8F9FA', boxShadow: neuShadowSm, cursor: 'pointer',
              }}
              onChange={async (ev) => {
                if (ev.target.value) {
                  await window.zignagenixAPI.moveContactStage(c.id, ev.target.value);
                  loadData(); loadExtended();
                }
              }}
              defaultValue=""
            >
              <option value="">Move to…</option>
              {stages.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          )}
        </div>
      ))}
    </div>
  )}
    </div>
  );
}
